import styles from "../../styles/CoordinationBoards.module.css"
import { useContext, useState, useEffect, useRef, useCallback } from "react"
import { UserContext } from "../../context/userContext"
import axios from "axios"
import { coordinationBoardsApiUrl } from "../../config/microservicesUrls"
import { LoaderMedium } from "../../components/Loaders"
import { Toast } from "primereact/toast"
import { Tag } from "primereact/tag"
import { InputSwitch } from "primereact/inputswitch"
import { Button } from "primereact/button"
import {
  showError,
  formatToLocalDateTime,
  DateTimeFormat,
  staffNameInitials,
  capitalizeFirstLetter,
  findName,
  renderTag,
  getMentions,
} from "./Utils/Utils"
import TopicForm from "./TopicForm"
import { Actions, TopicStatus } from "./Types/Enums"
import { Dialog } from "primereact/dialog"
import CommentField from "./Utils/CommentField"

const api_url = coordinationBoardsApiUrl()

const TopicView = ({
  topic,
  topicUpdate,
  lastActivityUpdate,
  closeView,
  defaultTags,
  groupedParticipants,
  board,
  canEditTopic,
  getStaffsNames,
  justStatusEdits,
  staffSuggestions,
}) => {
  const userCtx = useContext(UserContext)
  const [loadingComments, setLoadingComments] = useState(true)
  const [comments, setComments] = useState([])
  const [addingNewComment, setAddingNewComment] = useState(false)
  const toast = useRef(null)
  const [showTopicForm, setShowTopicForm] = useState(false)
  const [newCommentText, setNewCommentText] = useState("")
  const [editComment, setEditComment] = useState({})
  const [prevComment, setPrevComment] = useState({})
  const [commentToDelete, setCommentToDelete] = useState(null)
  const [followLoading, setFollowLoading] = useState(false)
  const [following, setFollowing] = useState(topic.following)
  const readOnly = topic.status !== TopicStatus.Active || board.readOnly

  const canEditComment = useCallback(
    (commentCreator) => {
      if (topic.status !== TopicStatus.Active || board.readOnly) return false
      const isAllowedToEdit = userCtx.session.user.staff_role <= 3 // org admins are the lowest roles to be able to edit
      return commentCreator === userCtx.session.user.staff_id || isAllowedToEdit
    },
    [
      userCtx.session.user.staff_id,
      userCtx.session.user.staff_role,
      topic,
      board,
    ],
  )

  const getActualStaffName = {
    first: userCtx.session.user.staff_name_first,
    last: userCtx.session.user.staff_name_last,
    full_name: `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`,
  }

  useEffect(() => {
    setEditComment({})
    setPrevComment({})
    setLoadingComments(true)
    const config = {
      headers: {
        Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
      },
    }
    axios
      .get(`${api_url}/topics/${topic.id}/comments`, config)
      .then(async (response) => {
        if (response.status === 200) {
          let _comments = await response.data.comments
          const staffIds = _comments.map((comment) => {
            let commentStaffIds = [comment.createdBy]
            if (comment.updatedBy != null) {
              commentStaffIds.push(comment.updatedBy)
            }
            return commentStaffIds
          })
          const names = await getStaffsNames(staffIds.flat())
          _comments = _comments.map((comment) => {
            if (comment.createdBy) {
              comment["createdByName"] = findName(comment.createdBy, names)
            }
            if (comment.updatedBy) {
              comment.updatedBy = findName(comment.updatedBy, names)
            }
            return comment
          })
          setComments(_comments)
        }
        setLoadingComments(false)
      })
      .catch(function (response) {
        setLoadingComments(false)
        const errors = response["response"]["data"]["errors"]
        showError(toast, "Not able to get Comments", errors)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic])

  const addComment = (data) => {
    const createdByName = getActualStaffName
    const _comments = [{ ...data, createdByName: createdByName }, ...comments]
    setComments(_comments)
    lastActivityUpdate({
      ...topic,
      newActivity: true,
      lastActivity: data.topicLastActivity,
      lastActivityBy: userCtx.session.user.staff_id,
      comments: _comments,
    })
  }

  const updateComment = (data) => {
    const updatedBy = getActualStaffName
    const updatedRecords = comments.map((c) => {
      if (data.id === c.id) {
        return { ...data, updatedBy: updatedBy, createdByName: c.createdByName }
      }
      return c
    })
    setComments(updatedRecords)
    lastActivityUpdate({
      ...topic,
      newActivity: true,
      lastActivity: data.topicLastActivity,
      lastActivityBy: userCtx.session.user.staff_id,
      comments: updatedRecords,
    })
  }

  const updateTopic = (data) => {
    const lastUpdatedBy = getActualStaffName
    topicUpdate({
      ...data,
      createdByName: topic.createdByName,
      lastUpdatedBy: lastUpdatedBy,
    })
  }

  const handleUpdateComment = (comment) => {
    const data = {
      id: comment.id,
      messageText: editComment[comment.id],
      topicId: topic.id,
      newMentions: getMentions(editComment[comment.id], staffSuggestions),
      oldMentions: getMentions(prevComment[comment.id], staffSuggestions),
      updatedBy: userCtx.session.user.staff_id,
      updatedByOrganization: userCtx.session.user.organization_id,
    }
    axios
      .put(`${api_url}/comments`, data, {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
        },
      })
      .then(function (response) {
        updateComment(response.data)
        setPrevComment({ ...prevComment, [comment.id]: undefined })
        setEditComment({ ...editComment, [comment.id]: undefined })
        setFollowing(true)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        showError(toast, errors, errors)
      })
  }

  const handleAddComment = () => {
    setAddingNewComment(true)

    const data = {
      messageText: newCommentText,
      topicId: topic.id,
      createdBy: userCtx.session.user.staff_id,
      createdByOrganization: userCtx.session.user.organization_id,
      mentions: getMentions(newCommentText, staffSuggestions),
    }

    axios
      .post(`${api_url}/comments`, data, {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
        },
      })
      .then(function (response) {
        setNewCommentText("")
        addComment(response.data)
        setFollowing(true)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        showError(toast, errors, errors)
      })
      .finally(() => {
        setAddingNewComment(false)
      })
  }

  const deleteComment = () => {
    axios
      .delete(`${api_url}/comments/?commentId=${commentToDelete}`, {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
        },
      })
      .then(function (response) {
        const updatedRecords = comments.filter((c) => c.id !== commentToDelete)
        setComments(updatedRecords)
      })
      .catch(function (response) {
        const errors = response["response"]["data"]["errors"]
        showError(toast, "Not able to delete comment", errors)
      })
  }

  const renderDeleteCommentConfirmation = () => {
    const footerContent = (
      <div className={styles.buttons}>
        <Button
          className={styles.cancel_button}
          label="No"
          onClick={() => setCommentToDelete(null)}
          autoFocus
        />
        <Button
          className={styles.button}
          label="Yes"
          onClick={() => {
            deleteComment()
            setCommentToDelete(null)
          }}
        />
      </div>
    )
    return (
      <Dialog
        header="Delete confirmation"
        modal={false}
        visible={commentToDelete !== null}
        className="custom_dialog"
        style={{ width: "50vw" }}
        onHide={() => setCommentToDelete(null)}
        footer={footerContent}
      >
        <p>{`Are you sure you want to delete the comment?`}</p>
      </Dialog>
    )
  }

  const renderCommentActions = (comment) => {
    if (canEditComment(comment.createdBy)) {
      return (
        <>
          {renderDeleteCommentConfirmation()}
          <div className={styles.comment_actions}>
            <Button
              className={styles.edit_btn}
              onClick={() => {
                setPrevComment({
                  ...prevComment,
                  [comment.id]: comment.messageText,
                })
                setEditComment({
                  ...editComment,
                  [comment.id]: comment.messageText,
                })
              }}
            />
            <Button
              icon="pi pi-trash"
              className={styles.rounded_btn}
              onClick={() => {
                setCommentToDelete(comment.id)
              }}
            />
          </div>
        </>
      )
    }
    return
  }

  const handleFollowDiscussion = (e) => {
    const follow = e.value
    setFollowing(follow)
    setFollowLoading(true)
    const data = {
      staffId: userCtx.session.user.staff_id,
      organizationId: userCtx.session.user.organization_id,
      unfollow: !follow,
    }
    axios
      .post(`${api_url}/topics/${topic.id}/follow`, data, {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getCoordinationBoardsToken()}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          const message = follow
            ? "You are now following this discussion"
            : "You are no longer following this discussion"
          toast.current.show({
            severity: "success",
            detail: message,
            life: 3000,
          })
        }
      })
      .catch((response) => {
        setFollowing(!follow)
        const errors = response["response"]["data"]["errors"]
        showError(toast, "Error while following discussion", errors)
      })
      .finally(() => {
        setFollowLoading(false)
      })
  }

  const renderTags = (tags) => {
    let participantTags = tags.filter((tag) => tag.type === 1)
    let otherTags = tags.filter((tag) => tag.type !== 1)
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {otherTags.map((tag) => {
          return (
            <Tag
              key={`${tag.id}`}
              value={renderTag(tag)}
              className={styles.tag}
            />
          )
        })}
        {participantTags.map((tag) => {
          return (
            <Tag
              key={`${tag.id}`}
              value={renderTag(tag)}
              className={styles.tag}
              style={{ backgroundColor: "#D8E6FD", color: "#0a306d87" }}
            />
          )
        })}
      </div>
    )
  }

  return (
    <>
      <Toast ref={toast} />
      <TopicForm
        board={board}
        updateTopic={updateTopic}
        action={Actions.Edit}
        setDialogVisible={setShowTopicForm}
        dialogVisible={showTopicForm}
        toast={toast}
        topic={topic}
        defaultTags={defaultTags}
        groupedParticipants={groupedParticipants}
        justStatusEdits={justStatusEdits}
        staffSuggestions={staffSuggestions}
      />
      <div className={styles.topic}>
        <button
          className={styles.back_link}
          onClick={() => closeView(following)}
        >
          <i className="pi pi-angle-left"></i>
          <p>{board.name}</p>
        </button>
        <hr style={{ marginTop: "10px" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            margin: "10px 0",
          }}
        >
          <h1>{topic.subject}</h1>
          {!readOnly && canEditTopic && (
            <Button
              label="Edit discussion"
              className={styles.button}
              style={{ height: "30px" }}
              onClick={() => {
                setShowTopicForm(true)
              }}
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <div className={styles.topic_header}>
            <span className={styles.topic_author_initials}>
              {staffNameInitials(topic.createdByName)}
            </span>
            <span className={styles.topic_author}>
              {topic.createdByName.full_name}
            </span>
            <span className={styles.topic_date}>
              {formatToLocalDateTime(topic.createdAt, DateTimeFormat)}
            </span>
          </div>
          {!readOnly && (
            <div className={styles.following_switch}>
              <label>Follow</label>
              <InputSwitch
                checked={following}
                className="custom-slider"
                onChange={handleFollowDiscussion}
                disabled={followLoading}
              />
            </div>
          )}
        </div>
        <div
          className="topic_description"
          dangerouslySetInnerHTML={{
            __html:
              topic.messageText != null
                ? capitalizeFirstLetter(topic.messageText, true)
                : "",
          }}
        />
        <div className={styles.tag_container}>{renderTags(topic.tags)}</div>
        {topic.lastUpdatedBy !== null && (
          <p className={styles.last_edited_by}>
            Last edited by <strong>{topic.lastUpdatedBy.full_name}</strong> on{" "}
            {formatToLocalDateTime(topic.lastUpdatedAt, DateTimeFormat)}
          </p>
        )}
        {topic.lastUpdatedReason !== null && (
          <p
            className={styles.updated_reason}
          >{`Reason: ${topic.lastUpdatedReason}`}</p>
        )}
        <hr />
        {loadingComments ? (
          <LoaderMedium></LoaderMedium>
        ) : (
          <>
            <h2>
              Comments{" "}
              {comments.length > 0 ? (
                <span className={styles.comments_count}>{comments.length}</span>
              ) : (
                ""
              )}
            </h2>
            {!readOnly && (
              <div className={styles.comment_input}>
                <CommentField
                  value={newCommentText}
                  disabled={addingNewComment}
                  placeholder="Write your comment here. Enter @ to mention people."
                  onTextChange={(e) => {
                    setNewCommentText(e)
                  }}
                  suggestions={staffSuggestions}
                />
                <Button
                  label="Add Comment"
                  onClick={handleAddComment}
                  disabled={newCommentText === "" || addingNewComment}
                  loading={addingNewComment}
                  className={`${styles.button} ${styles.add_comment_button}`}
                />
              </div>
            )}
            {comments.length > 0 && (
              <div className={styles.comments_container}>
                {comments.map((comment) => (
                  <div key={comment.id} className={styles.comment}>
                    <div style={{ maxWidth: "calc(90vw - 250px)" }}>
                      <div className={styles.comment_header}>
                        <span className={styles.comment_author_initials}>
                          {staffNameInitials(comment.createdByName)}
                        </span>
                        <span className={styles.comment_author}>
                          {comment.createdByName.full_name}
                        </span>
                        <span className={styles.comment_date}>
                          {formatToLocalDateTime(
                            comment.createdAt,
                            DateTimeFormat,
                          )}
                        </span>
                      </div>
                      {editComment[comment.id] ? (
                        <div
                          className={styles.comment_input_edit}
                          style={{ paddingLeft: "29px", width: "100%" }}
                        >
                          <CommentField
                            value={editComment[comment.id]}
                            placeholder="Edit your comment here. Enter @ to mention people."
                            onTextChange={(e) => {
                              setEditComment({
                                ...editComment,
                                [comment.id]: e,
                              })
                            }}
                            suggestions={staffSuggestions}
                          />
                          <Button
                            icon="pi pi-check"
                            onClick={() => {
                              handleUpdateComment(comment)
                            }}
                            className={`${styles.rounded_btn} ${styles.update_comment_button}`}
                          />
                          <Button
                            icon="pi pi-times"
                            className={`${styles.rounded_btn} ${styles.close_edit_button}`}
                            onClick={() => {
                              setEditComment({
                                ...editComment,
                                [comment.id]: undefined,
                              })
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ paddingLeft: "29px" }}>
                          <div
                            className="topic_description"
                            dangerouslySetInnerHTML={{
                              __html:
                                comment.messageText != null
                                  ? comment.messageText
                                  : "",
                            }}
                          />
                          {/* <p className={styles.comment_text}>{comment.messageText}</p> */}
                          {comment.updatedBy && (
                            <p
                              className={styles.last_edited_by}
                              style={{
                                fontSize: "12px",
                                marginTop: "8px",
                                color: "#383838c4",
                              }}
                            >
                              Last edited by{" "}
                              <strong>{comment.updatedBy.full_name}</strong> on{" "}
                              {formatToLocalDateTime(
                                comment.updatedAt,
                                DateTimeFormat,
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    {!editComment[comment.id] && renderCommentActions(comment)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default TopicView
